import React, { useEffect } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import Svg, { Circle, Path } from 'react-native-svg';
import Animated, {
  useSharedValue,
  useAnimatedProps,
  withRepeat,
  withTiming,
  withDelay,
  Easing,
} from 'react-native-reanimated';
import { withDefaultIconProps } from './index';

const AnimatedPath = Animated.createAnimatedComponent(Path);
const SignalWithDefaultProps = withDefaultIconProps(
  {
    height: 30,
    width: 30,
  },
  ({
    color,
    style,
    ...rest
  }: {
    color?: string;
    fillColor?: string;
    style?: StyleProp<ViewStyle>;
    [key: string]: unknown;
  }) => (
    <Svg fill={color || '#1F2635'} style={style} {...rest} viewBox="0 0 80 80">
      <Path
        d="M40 68.7C41.13 68.7 42.04 67.79 42.04 66.67V37.7C44.42 36.86 46.14 34.58 46.14 31.91C46.14 28.53 43.38 25.77 40 25.77C36.62 25.77 33.86 28.53 33.86 31.91C33.86 34.58 35.58 36.86 37.96 37.7V66.67C37.96 67.79 38.88 68.7 40 68.7Z"
        fill={color || '#1F2635'}
      />
      <Path
        d="M49.87 44.56C50.27 44.96 50.79 45.16 51.31 45.16C51.84 45.16 52.36 44.96 52.75 44.56C56.16 41.16 58.04 36.63 58.04 31.81C58.04 26.99 56.16 22.46 52.75 19.06C51.96 18.26 50.67 18.26 49.87 19.06C49.08 19.85 49.08 21.14 49.87 21.94C55.32 27.38 55.32 36.24 49.87 41.68C49.08 42.48 49.08 43.77 49.87 44.56Z"
        fill={color || '#1F2635'}
      />
      <Path
        d="M30.13 19.06C29.33 18.26 28.04 18.26 27.25 19.06C23.84 22.46 21.96 26.99 21.96 31.81C21.96 36.63 23.84 41.16 27.25 44.56C27.64 44.96 28.16 45.16 28.69 45.16C29.21 45.16 29.73 44.96 30.13 44.56C30.92 43.77 30.92 42.48 30.13 41.68C24.68 36.24 24.68 27.38 30.13 21.94C30.92 21.14 30.92 19.85 30.13 19.06Z"
        fill={color || '#1F2635'}
      />
      <Path
        d="M60.3 11.51C59.5 10.72 58.21 10.72 57.42 11.51C56.62 12.31 56.62 13.6 57.42 14.39C62.07 19.05 64.63 25.23 64.63 31.81C64.63 38.39 62.07 44.57 57.42 49.23C56.62 50.02 56.62 51.31 57.42 52.11C57.81 52.5 58.33 52.7 58.86 52.7C59.38 52.7 59.9 52.5 60.3 52.11C63.04 49.37 65.16 46.17 66.6 42.61C68 39.17 68.7 35.54 68.7 31.81C68.7 28.08 68 24.45 66.6 21.01C65.16 17.45 63.04 14.25 60.3 11.51Z"
        fill={color || '#1F2635'}
      />
      <Path
        d="M11.3 31.81C11.3 35.54 12 39.17 13.4 42.61C14.84 46.17 16.96 49.37 19.7 52.11C20.1 52.5 20.62 52.7 21.14 52.7C21.67 52.7 22.19 52.5 22.58 52.11C23.38 51.31 23.38 50.02 22.58 49.23C17.93 44.57 15.37 38.39 15.37 31.81C15.37 25.23 17.93 19.05 22.58 14.39C23.38 13.6 23.38 12.31 22.58 11.51C21.79 10.72 20.5 10.72 19.7 11.51C16.96 14.25 14.84 17.45 13.4 21.01C12 24.45 11.3 28.08 11.3 31.81Z"
        fill={color || '#1F2635'}
      />
    </Svg>
  ),
);

export function Signal(props: React.ComponentProps<typeof SignalWithDefaultProps>) {
  return <SignalWithDefaultProps {...props} />;
}

const PULSE_DURATION = 800; // one half-cycle (rise or fall)
const PULSE_STAGGER = 200; // quarter-cycle offset between inner and outer rings

export function AnimatedSignal({
  color = '#1F2635',
  fillColor,
  style,
  height = 30,
  width = 30,
  enableAnimation = true,
}: {
  color?: string;
  fillColor?: string;
  style?: StyleProp<ViewStyle>;
  height?: number;
  width?: number;
  enableAnimation?: boolean;
}) {
  const innerOpacity = useSharedValue(0);
  const outerOpacity = useSharedValue(0);

  useEffect(() => {
    if (!enableAnimation) {
      innerOpacity.set(1);
      outerOpacity.set(1);
      return;
    }

    const pulse = (delay: number) =>
      withDelay(
        delay,
        withRepeat(withTiming(1, { duration: PULSE_DURATION, easing: Easing.inOut(Easing.sin) }), -1, true),
      );

    innerOpacity.set(pulse(0));
    outerOpacity.set(pulse(PULSE_STAGGER));
  }, [innerOpacity, outerOpacity, enableAnimation]);

  const innerWaveProps = useAnimatedProps(() => ({
    opacity: innerOpacity.get(),
  }));

  const outerWaveProps = useAnimatedProps(() => ({
    opacity: outerOpacity.get(),
  }));

  return (
    <Svg fill={color} style={style} height={height} width={width} viewBox="0 0 80 80">
      {fillColor ? <Circle cx={40} cy={40} r={40} fill={fillColor} /> : null}
      {/* Center pin — always visible */}
      <Path
        d="M40 68.7C41.13 68.7 42.04 67.79 42.04 66.67V37.7C44.42 36.86 46.14 34.58 46.14 31.91C46.14 28.53 43.38 25.77 40 25.77C36.62 25.77 33.86 28.53 33.86 31.91C33.86 34.58 35.58 36.86 37.96 37.7V66.67C37.96 67.79 38.88 68.7 40 68.7Z"
        fill={color}
      />
      {/* Inner right wave */}
      <AnimatedPath
        animatedProps={innerWaveProps}
        d="M49.87 44.56C50.27 44.96 50.79 45.16 51.31 45.16C51.84 45.16 52.36 44.96 52.75 44.56C56.16 41.16 58.04 36.63 58.04 31.81C58.04 26.99 56.16 22.46 52.75 19.06C51.96 18.26 50.67 18.26 49.87 19.06C49.08 19.85 49.08 21.14 49.87 21.94C55.32 27.38 55.32 36.24 49.87 41.68C49.08 42.48 49.08 43.77 49.87 44.56Z"
        fill={color}
      />
      {/* Inner left wave */}
      <AnimatedPath
        animatedProps={innerWaveProps}
        d="M30.13 19.06C29.33 18.26 28.04 18.26 27.25 19.06C23.84 22.46 21.96 26.99 21.96 31.81C21.96 36.63 23.84 41.16 27.25 44.56C27.64 44.96 28.16 45.16 28.69 45.16C29.21 45.16 29.73 44.96 30.13 44.56C30.92 43.77 30.92 42.48 30.13 41.68C24.68 36.24 24.68 27.38 30.13 21.94C30.92 21.14 30.92 19.85 30.13 19.06Z"
        fill={color}
      />
      {/* Outer right wave */}
      <AnimatedPath
        animatedProps={outerWaveProps}
        d="M60.3 11.51C59.5 10.72 58.21 10.72 57.42 11.51C56.62 12.31 56.62 13.6 57.42 14.39C62.07 19.05 64.63 25.23 64.63 31.81C64.63 38.39 62.07 44.57 57.42 49.23C56.62 50.02 56.62 51.31 57.42 52.11C57.81 52.5 58.33 52.7 58.86 52.7C59.38 52.7 59.9 52.5 60.3 52.11C63.04 49.37 65.16 46.17 66.6 42.61C68 39.17 68.7 35.54 68.7 31.81C68.7 28.08 68 24.45 66.6 21.01C65.16 17.45 63.04 14.25 60.3 11.51Z"
        fill={color}
      />
      {/* Outer left wave */}
      <AnimatedPath
        animatedProps={outerWaveProps}
        d="M11.3 31.81C11.3 35.54 12 39.17 13.4 42.61C14.84 46.17 16.96 49.37 19.7 52.11C20.1 52.5 20.62 52.7 21.14 52.7C21.67 52.7 22.19 52.5 22.58 52.11C23.38 51.31 23.38 50.02 22.58 49.23C17.93 44.57 15.37 38.39 15.37 31.81C15.37 25.23 17.93 19.05 22.58 14.39C23.38 13.6 23.38 12.31 22.58 11.51C21.79 10.72 20.5 10.72 19.7 11.51C16.96 14.25 14.84 17.45 13.4 21.01C12 24.45 11.3 28.08 11.3 31.81Z"
        fill={color}
      />
    </Svg>
  );
}

export default Signal;
